import { Link } from 'react-router-dom';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaChevronDown, 
  FaChevronUp, 
  FaSearch,
  FaQuestionCircle,
  FaEnvelope,
  FaPhone,
  FaWhatsapp,
  FaHeadset,
  FaClock,
  FaBuilding,
  FaUniversity,
  FaRocket,
  FaCloud,
  FaMoneyBillWave,
  FaMobileAlt,
  FaUsers,
  FaCode,
  FaGlobeAfrica,
  FaHandshake,
  FaStar,
  FaTimes
} from 'react-icons/fa';

const faqData = [
  {
    id: 'general',
    label: 'General',
    icon: FaBuilding,
    questions: [
      {
        question: 'What does Micropoint Systems do?',
        answer: 'Micropoint Systems provides business and financial technology solutions for organizations including SACCOs, microfinance institutions, financial businesses, schools and enterprises. Our solutions cover financial management, lending, payroll, enterprise systems, mobile applications, integrations and custom software development.'
      },
      {
        question: 'How long has Micropoint been in business?',
        answer: 'Micropoint has been in business since 2012, with experience delivering technology solutions to organizations in Kenya and the wider region.'
      },
      {
        question: 'Where does Micropoint operate?',
        answer: 'Micropoint is based in Kenya and has experience supporting organizations across the region, including projects and clients in Uganda and Somalia.'
      },
      {
        question: 'Do you only provide ready-made software?',
        answer: 'No. We provide both product-based solutions and tailored technology. Our platforms can be configured and extended around the way each organization works, while we also undertake integrations and bespoke software development where required.'
      },
      {
        question: 'Can Micropoint customize its solutions?',
        answer: 'Yes. Configurability and extensibility are among our key strengths. We can tailor workflows, products, charges, reports, integrations and other functionality to suit an organization\'s requirements.'
      },
      {
        question: 'Do you provide implementation and training?',
        answer: 'Yes. Our implementation experience includes requirements gathering, configuration, data migration, user training, testing, commissioning and ongoing support.'
      },
      {
        question: 'Can you migrate data from our existing system?',
        answer: 'Yes. Data migration is part of our implementation experience. The approach depends on the existing system, data quality, scope and the information that needs to be retained.'
      }
    ]
  },
  {
    id: 'powerfinancials',
    label: 'PowerFinancials',
    icon: FaUniversity,
    questions: [
      {
        question: 'What is PowerFinancials?',
        answer: 'PowerFinancials is Micropoint\'s financial management platform designed to support organizations such as SACCOs, microfinance institutions and other financial businesses.'
      },
      {
        question: 'What can PowerFinancials manage?',
        answer: 'PowerFinancials can support member/customer management, savings and accounts, lending and loan management, repayments, charges and fees, financial accounting, general ledger, reporting, security and user roles, digital channels and integrations.'
      },
      {
        question: 'Is PowerFinancials suitable for SACCOs?',
        answer: 'Yes. SACCOs are one of the core areas in which Micropoint has extensive experience.'
      },
      {
        question: 'Can PowerFinancials support microfinance institutions?',
        answer: 'Yes. PowerFinancials has been used by microfinance and credit organizations, including organizations operating in Kenya and the wider region.'
      },
      {
        question: 'Can PowerFinancials handle different loan products?',
        answer: 'Yes. Loan products can be configured around different lending requirements, including applicable charges, repayment structures and other product rules.'
      },
      {
        question: 'Can PowerFinancials handle loan top-ups?',
        answer: 'Yes. PowerFinancials can support loan top-up workflows, including the treatment of existing outstanding balances and applicable charges.'
      },
      {
        question: 'Can PowerFinancials handle loan restructuring or rescheduling?',
        answer: 'Yes. The lending environment can accommodate different loan-management workflows, subject to the organization\'s specific configuration and policies.'
      },
      {
        question: 'Can I configure charges in PowerFinancials?',
        answer: 'Yes. Charges can be configured according to the organization\'s products and business rules.'
      },
      {
        question: 'Can PowerFinancials integrate with M-PESA?',
        answer: 'Yes. Micropoint has experience integrating financial systems with M-PESA and other digital payment channels.'
      },
      {
        question: 'Can PowerFinancials integrate with banks?',
        answer: 'Yes. Micropoint has experience integrating financial systems with banking platforms for transaction notifications, payments and other financial workflows.'
      },
      {
        question: 'Can PowerFinancials integrate with CRBs?',
        answer: 'Yes. Micropoint has experience integrating financial systems with credit-reference services, including workflows involving identity verification, delinquency checks, credit scores and credit reports.'
      },
      {
        question: 'Can PowerFinancials support mobile lending?',
        answer: 'Yes. Micropoint has delivered mobile lending solutions, including the Mkopo Radi solution for Bands SACCO.'
      },
      {
        question: 'Can PowerFinancials support multiple branches?',
        answer: 'Yes. The solution can support organizations operating across multiple branches, subject to the implementation architecture and configuration.'
      },
      {
        question: 'Can PowerFinancials support role-based security?',
        answer: 'Yes. User access and operational permissions can be configured around organizational roles and responsibilities.'
      }
    ]
  },
  {
    id: 'implementation',
    label: 'Implementation & Migration',
    icon: FaRocket,
    questions: [
      {
        question: 'How long does implementation take?',
        answer: 'Implementation time varies depending on the organization\'s size, complexity, modules required, integrations and data-migration requirements. After understanding your requirements, Micropoint can provide an implementation plan and timeline.'
      },
      {
        question: 'Do you migrate historical data?',
        answer: 'Yes, where required. We work with the client to determine what historical information needs to be migrated and how it should be validated.'
      },
      {
        question: 'Do you first study our existing processes?',
        answer: 'Yes. Requirements gathering and understanding the organization\'s processes are important parts of implementation.'
      },
      {
        question: 'Can you integrate our existing systems instead of replacing everything?',
        answer: 'Yes. Where appropriate, Micropoint can integrate existing systems with its solutions rather than requiring an organization to replace every component.'
      },
      {
        question: 'What happens if our processes don\'t fit the standard system?',
        answer: 'This is where Micropoint\'s configurability and extensibility become particularly important. We first determine whether the requirement can be handled through configuration. Where it cannot, we can consider customization, extension or integration.'
      },
      {
        question: 'Do you provide support after implementation?',
        answer: 'Yes. Micropoint provides ongoing support and can continue working with clients as their requirements evolve.'
      }
    ]
  },
  {
    id: 'dynamics365',
    label: 'Dynamics 365 / Business Central',
    icon: FaCloud,
    questions: [
      {
        question: 'Does Micropoint implement Microsoft Dynamics 365?',
        answer: 'Yes. Micropoint provides Microsoft Dynamics 365 implementation, customization, integration and extension services.'
      },
      {
        question: 'Why would a financial institution choose Dynamics 365?',
        answer: 'Dynamics 365 can provide an enterprise platform for organizations that require broader capabilities, integration and extensibility across multiple business functions.'
      },
      {
        question: 'Can Micropoint customize Dynamics 365?',
        answer: 'Yes. Customization and extension are important parts of Micropoint\'s Dynamics 365 capability.'
      },
      {
        question: 'Can Dynamics 365 support finance and accounting?',
        answer: 'Yes. Finance and accounting can form part of the Dynamics environment, depending on the specific Microsoft Dynamics products selected.'
      },
      {
        question: 'Can Dynamics 365 support procurement?',
        answer: 'Yes. Procurement functionality can be incorporated into an appropriately designed Dynamics environment.'
      },
      {
        question: 'Can Dynamics 365 support HR and payroll?',
        answer: 'Yes, depending on the Microsoft products, applications and integrations selected for the implementation.'
      },
      {
        question: 'Can Dynamics 365 support employee self-service?',
        answer: 'Yes. Employee self-service can be incorporated into an appropriately designed solution.'
      },
      {
        question: 'Can Dynamics 365 integrate with mobile applications?',
        answer: 'Yes. Micropoint can integrate enterprise systems with surrounding digital applications, including mobile solutions.'
      },
      {
        question: 'Can Dynamics 365 integrate with CRM?',
        answer: 'Yes. CRM capabilities can form part of a connected enterprise environment.'
      },
      {
        question: 'Do you have an actual Dynamics 365 implementation?',
        answer: 'Yes. Springboard Capital is an important Micropoint case study, with an implementation spanning credit, finance, procurement, HR and payroll, employee self-service, mobile lending and CRM.'
      }
    ]
  },
  {
    id: 'payments',
    label: 'Payments & Bank Integration',
    icon: FaMoneyBillWave,
    questions: [
      {
        question: 'Can Micropoint integrate our system with our bank?',
        answer: 'Yes. We can assess the bank\'s available integration mechanisms and design the appropriate connection between the banking platform and your business system.'
      },
      {
        question: 'What types of bank integration do you support?',
        answer: 'Depending on the bank and requirements, integrations can include transaction notifications, payment processing, account information, bank statements, collections, reconciliation and other bank-specific services.'
      },
      {
        question: 'Can incoming bank transactions be automatically posted?',
        answer: 'Yes, where the bank integration provides the required information. Transactions can be received, validated, matched and routed into the appropriate business workflow.'
      },
      {
        question: 'Can you automate payment reconciliation?',
        answer: 'Yes. Payment integration can be designed to match incoming transactions to the appropriate member, customer or account and identify exceptions requiring review.'
      },
      {
        question: 'Do you integrate with M-PESA?',
        answer: 'Yes. Micropoint has experience integrating M-PESA and mobile-money channels into financial workflows.'
      },
      {
        question: 'Do you support payment gateways?',
        answer: 'Yes. Payment gateways and other payment channels can be integrated depending on the specific service and technical requirements.'
      },
      {
        question: 'Do you support cheque truncation?',
        answer: 'Yes. Micropoint has historical experience delivering cheque truncation and remote cheque-processing solutions.'
      },
      {
        question: 'Have you integrated with banks before?',
        answer: 'Yes. Our selected integration experience includes work involving NCBA, Co-operative Bank, Family Bank, Equity Bank and KCB, as well as Metropol CRB.'
      },
      {
        question: 'Do you support ISO 8583?',
        answer: 'Micropoint has historical experience with ISO 8583-based financial transaction messaging, including payment-switching environments.'
      },
      {
        question: 'Is ISO 8583 obsolete?',
        answer: 'No. ISO 8583 is an established financial transaction messaging standard. Modern API-based integrations and established financial messaging standards can coexist depending on the architecture and requirements.'
      }
    ]
  },
  {
    id: 'mobile-lending',
    label: 'Mobile Lending',
    icon: FaMobileAlt,
    questions: [
      {
        question: 'Can Micropoint build a mobile lending application?',
        answer: 'Yes. Micropoint has experience delivering mobile lending solutions connected to financial systems.'
      },
      {
        question: 'Can the mobile application connect to the core system?',
        answer: 'Yes. A mobile lending application can be integrated with the underlying financial system so that applications, approvals, disbursements and repayments can participate in the appropriate workflows.'
      },
      {
        question: 'Can you build a mobile solution for an existing SACCO?',
        answer: 'Yes. The approach depends on the SACCO\'s existing system, security environment, APIs and integration requirements.'
      },
      {
        question: 'Do you have experience with highly secure environments?',
        answer: 'Yes. The Bands SACCO / Mkopo Radi project is an example of Micropoint delivering a mobile lending solution within a demanding security and integration environment.'
      }
    ]
  },
  {
    id: 'hr-payroll',
    label: 'HR & Payroll',
    icon: FaUsers,
    questions: [
      {
        question: 'Does Micropoint provide payroll software?',
        answer: 'Yes. Micropoint provides HR and payroll solutions for organizations of different sizes.'
      },
      {
        question: 'Can payroll earnings and deductions be configured?',
        answer: 'Yes. Payroll can accommodate configurable earnings and deductions according to organizational requirements.'
      },
      {
        question: 'Can payroll handle statutory deductions?',
        answer: 'Yes. Payroll can be configured to support applicable statutory and regulatory deductions, subject to the current regulatory rules and the organization\'s configuration.'
      },
      {
        question: 'Can you handle PAYE, SHIF/SHA and the Affordable Housing Levy?',
        answer: 'Yes. The payroll solution can support applicable Kenyan statutory deductions such as PAYE, SHIF/SHA and the Affordable Housing Levy, subject to the current regulatory rules and configuration.'
      },
      {
        question: 'Can payroll support large workforces?',
        answer: 'Yes. Micropoint\'s payroll experience includes Platinum Credit in Uganda, where the solution supports payroll for more than 1,500 agents in Kampala.'
      },
      {
        question: 'Can employees access their payroll information themselves?',
        answer: 'Yes. Employee Self-Service (ESS) can be incorporated where required.'
      },
      {
        question: 'Can payroll integrate with other systems?',
        answer: 'Yes. Payroll can be integrated with other business systems and payment processes depending on requirements.'
      }
    ]
  },
  {
    id: 'custom',
    label: 'Custom Software & Integration',
    icon: FaCode,
    questions: [
      {
        question: 'Can Micropoint build software from scratch?',
        answer: 'Yes. In addition to its packaged products, Micropoint has experience developing bespoke enterprise applications around specific organizational requirements.'
      },
      {
        question: 'Do you only work with financial institutions?',
        answer: 'No. Micropoint has experience across financial services, SACCOs, microfinance, education, enterprise systems and other business environments.'
      },
      {
        question: 'Can you integrate systems built by another software company?',
        answer: 'Yes. Subject to the available APIs, interfaces, documentation and access, Micropoint can assess integration with third-party systems.'
      },
      {
        question: 'Can you extend an existing application rather than replace it?',
        answer: 'Yes. Where technically and commercially appropriate, extending an existing system can be a better approach than replacing it.'
      },
      {
        question: 'Do you build APIs?',
        answer: 'Yes. APIs can form part of integration and application architectures developed by Micropoint.'
      }
    ]
  },
  {
    id: 'regional',
    label: 'Regional Experience',
    icon: FaGlobeAfrica,
    questions: [
      {
        question: 'Does Micropoint operate outside Kenya?',
        answer: 'Yes. Micropoint has experience delivering and supporting solutions in the wider East African and regional market.'
      },
      {
        question: 'Do you have experience in Uganda?',
        answer: 'Yes. Platinum Credit uses Micropoint\'s payroll solution to support more than 1,500 agents in Kampala.'
      },
      {
        question: 'Do you have experience in Somalia?',
        answer: 'Yes. KOBCIYE LLC, a Mogadishu-based microfinance institution, uses Micropoint\'s financial technology.'
      },
      {
        question: 'Can you adapt your software to different countries?',
        answer: 'Yes. The implementation approach can take account of the organization\'s local regulatory, operational, financial and integration requirements.'
      }
    ]
  },
  {
    id: 'support',
    label: 'Support & Long-Term Relationships',
    icon: FaHandshake,
    questions: [
      {
        question: 'Do you work with clients long term?',
        answer: 'Yes. Long-term relationships are an important part of Micropoint\'s history. Dream Credit is a particularly good example: after years of using PowerFinancials, Micropoint is now upgrading the client to the next generation of the platform.'
      },
      {
        question: 'What happens when our business outgrows the software?',
        answer: 'Micropoint\'s philosophy is that technology should evolve with the organization. Depending on the situation, we can configure, extend, integrate or migrate to a broader platform such as Microsoft Dynamics.'
      },
      {
        question: 'Do you provide customization after implementation?',
        answer: 'Yes. Organizations evolve, and Micropoint can continue to provide enhancements and extensions as requirements change.'
      },
      {
        question: 'Can you work with our internal IT team?',
        answer: 'Yes. The engagement model can be structured around the client\'s internal technology and operational teams.'
      },
      {
        question: 'How do we get started?',
        answer: 'Start by telling us what you are trying to achieve, what systems you currently use and the challenges you want to solve. Micropoint can then assess the requirements and recommend an appropriate approach.'
      }
    ]
  }
];

// ─── Key Differentiator ──────────────────────────────
const keyDifferentiator = {
  question: 'What makes Micropoint different from other software providers?',
  answer: 'We don\'t believe your business should have to fit the software. Micropoint combines proven software platforms with the ability to configure, extend and integrate them around the way your organization actually works. Our experience spans SACCOs, microfinance institutions, financial businesses, enterprise systems, payroll, mobile applications and banking integrations. We\'ve worked with organizations from their early stages of growth, helped others through major technology transformations, and continued evolving solutions for long-standing clients. Our goal is simple: technology that grows with your business.'
};

// ─── Component ─────────────────────────────────────────────

export default function FAQs() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('general');
  const [openQuestions, setOpenQuestions] = useState({});

  const toggleQuestion = (categoryId, index) => {
    const key = `${categoryId}-${index}`;
    setOpenQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isQuestionOpen = (categoryId, index) => {
    const key = `${categoryId}-${index}`;
    return openQuestions[key] || false;
  };

  const currentCategory = faqData.find(c => c.id === activeCategory);
  const CategoryIcon = currentCategory?.icon || FaBuilding;

  // Filter questions based on search
  const getFilteredQuestions = (category) => {
    if (!searchTerm) return category.questions;
    return category.questions.filter(q =>
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredQuestions = currentCategory ? getFilteredQuestions(currentCategory) : [];

  // Search across all categories
  const searchResults = searchTerm ? faqData.map(cat => ({
    ...cat,
    questions: getFilteredQuestions(cat)
  })).filter(cat => cat.questions.length > 0) : [];

  const totalResults = searchResults.reduce((acc, cat) => acc + cat.questions.length, 0);

  return (
    <section className="py-16 md:py-20 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-6xl">
        
        {/* ─── Header ─── */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-yellow-50 text-yellow-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 border border-yellow-200">
              Frequently Asked Questions
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 tracking-tight">
              Frequently Asked <span className="font-semibold text-yellow-600">Questions</span>
            </h1>
            <div className="w-16 h-1 bg-yellow-600 mx-auto mt-4 rounded-full" />
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-sm">
              Find answers to common questions about our solutions, implementation, integrations and services.
            </p>
          </motion.div>
        </div>

        {/* ─── Search ─── */}
        <div className="mb-8 max-w-2xl mx-auto">
          <div className="relative">
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-12 rounded-xl border border-gray-200 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100 outline-none transition-all bg-white text-sm placeholder:text-gray-400 shadow-sm"
            />
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="text-xs" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-sm text-gray-500 mt-2">
              Found <span className="font-medium text-gray-700">{totalResults}</span> results
            </p>
          )}
        </div>

        {/* ─── Main Content - Sidebar Layout ─── */}
        <div className="grid lg:grid-cols-4 gap-6 md:gap-8">
          
          {/* ─── Left Sidebar ─── */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden sticky top-24"
            >
              {/* Sidebar Header */}
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-yellow-600 to-amber-600">
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  <FaHeadset className="text-white" />
                  FAQ Categories
                </h3>
              </div>
              
              {/* Category List */}
              <div className="p-3 max-h-[500px] overflow-y-auto">
                {faqData.map((category) => {
                  const Icon = category.icon;
                  const isActive = activeCategory === category.id;
                  const count = category.questions.length;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 text-sm ${
                        isActive
                          ? 'bg-yellow-50 text-yellow-700 font-semibold border border-yellow-200'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-yellow-600'
                      }`}
                    >
                      <Icon className={`text-lg ${isActive ? 'text-yellow-600' : 'text-gray-400'}`} />
                      <span className="flex-1 text-left">{category.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        isActive ? 'bg-yellow-200 text-yellow-700' : 'bg-gray-100 text-gray-400'
                      }`}>
                        {count}
                      </span>
                      {isActive && (
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-600 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* ─── Right Content ─── */}
          <div className="lg:col-span-3">
            
            {/* ─── Key Differentiator ─── */}
            {!searchTerm && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 border border-yellow-100"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <FaStar className="text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{keyDifferentiator.question}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{keyDifferentiator.answer}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ─── Results Count ─── */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                {searchTerm ? `${totalResults} results found` : `${filteredQuestions.length} questions`}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-xs text-yellow-600 hover:text-yellow-700 transition-colors"
                >
                  Clear search
                </button>
              )}
            </div>

            {/* ─── FAQ Content ─── */}
            {searchTerm ? (
              // Search Results
              <div className="space-y-6">
                {searchResults.map((category) => (
                  <div key={category.id}>
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <category.icon className="text-yellow-600 text-xs" />
                      {category.label}
                    </h3>
                    <div className="space-y-2">
                      {category.questions.map((q, idx) => {
                        const isOpen = isQuestionOpen(`search-${category.id}`, idx);
                        return (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2, delay: idx * 0.05 }}
                            className="bg-white rounded-xl border border-gray-200 hover:border-yellow-300 transition-all overflow-hidden shadow-sm"
                          >
                            <button
                              onClick={() => toggleQuestion(`search-${category.id}`, idx)}
                              className="w-full px-5 py-3.5 text-left flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors"
                            >
                              <span className="text-sm font-medium text-gray-800">{q.question}</span>
                              <span className={`text-yellow-600 text-xs transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}>
                                <FaChevronDown />
                              </span>
                            </button>
                            <AnimatePresence>
                              {isOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="px-5 pb-4"
                                >
                                  <div className="pt-3 border-t border-gray-100">
                                    <p className="text-sm text-gray-600 leading-relaxed">{q.answer}</p>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {totalResults === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <div className="text-4xl mb-3">🔍</div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">No FAQs Found</h3>
                    <p className="text-sm text-gray-500">Try adjusting your search terms.</p>
                  </div>
                )}
              </div>
            ) : (
              // Category View
              currentCategory && (
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center">
                      <CategoryIcon className="text-yellow-600 text-lg" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">{currentCategory.label}</h2>
                    <span className="bg-yellow-50 text-yellow-700 text-xs px-3 py-1 rounded-full font-medium">
                      {currentCategory.questions.length}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {currentCategory.questions.map((q, index) => {
                      const isOpen = isQuestionOpen(activeCategory, index);
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: index * 0.03 }}
                          className="bg-white rounded-xl border border-gray-200 hover:border-yellow-300 transition-all overflow-hidden shadow-sm hover:shadow-md"
                        >
                          <button
                            onClick={() => toggleQuestion(activeCategory, index)}
                            className="w-full px-5 py-3.5 text-left flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors"
                          >
                            <span className="text-sm font-medium text-gray-800">{q.question}</span>
                            <span className={`text-yellow-600 text-xs transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}>
                              <FaChevronDown />
                            </span>
                          </button>
                          <AnimatePresence>
                            {isOpen && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3 }}
                                className="px-5 pb-4"
                              >
                                <div className="pt-3 border-t border-gray-100">
                                  <p className="text-sm text-gray-600 leading-relaxed">{q.answer}</p>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )
            )}

            {/* ─── CTA Section ─── */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative overflow-hidden rounded-2xl p-8 md:p-10 text-center text-white mt-10"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,193,7,0.06)_0%,_transparent_70%)]" />
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 border border-white/10">
                  <FaQuestionCircle className="text-xl text-yellow-400" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2 text-white">
                  Still have a <span className="text-yellow-400">question</span>?
                </h3>
                <p className="text-white/50 text-sm mb-6 max-w-lg mx-auto">
                  Tell us what you are trying to achieve. Our team will help you identify the right technology approach.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link 
                    to="/contact" 
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-3 rounded-full font-semibold text-sm transition-all hover:shadow-lg hover:shadow-yellow-500/30 hover:-translate-y-0.5"
                  >
                    Talk to Our Team
                  </Link>
                  <Link 
                    to="/demo" 
                    className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-full font-semibold text-sm border border-white/20 hover:border-white/30 transition-all hover:-translate-y-0.5"
                  >
                    Request a Demo
                  </Link>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </section>
  );
}